export interface ChannelInteraction {
  value: string;
  label: string;
  isLocked: boolean;
  checked: boolean;
}

export const interactionsByChannel: Record<string, ChannelInteraction[]> = {
  instagram: [
    { value: 'ExternalComment', label: 'Comentários externos', isLocked: false, checked: true },
    { value: 'Inbox', label: 'Inbox', isLocked: false, checked: true },
    { value: 'Mention', label: 'Menções', isLocked: false, checked: true },
    { value: 'MentionComment', label: 'Comentários de Menções', isLocked: false, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'Reels', label: 'Reels', isLocked: true, checked: true },
  ],
  tiktok: [
    { value: 'Inbox', label: 'Inbox', isLocked: false, checked: true },
    { value: 'ExternalComment', label: 'Comentários externos', isLocked: false, checked: true },
    { value: 'Mention', label: 'Menções', isLocked: false, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
  ],
  facebook: [
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
    { value: 'Mention', label: 'Menções', isLocked: false, checked: true },
    { value: 'Inbox', label: 'Inbox', isLocked: false, checked: true },
    { value: 'VisitorPost', label: 'Publicações de visitantes', isLocked: true, checked: true },
    { value: 'VisitorComment', label: 'Comentários em publicações de visitantes', isLocked: true, checked: true },
    { value: 'Review', label: 'Avaliações', isLocked: true, checked: true },
    { value: 'ReviewComment', label: 'Comentários em avaliações', isLocked: true, checked: true },
    { value: 'MentionComment', label: 'Comentários em menções', isLocked: false, checked: true },
  ],
  twitter: [
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'Inbox', label: 'Inbox', isLocked: false, checked: true },
    { value: 'Mention', label: 'Menções', isLocked: false, checked: true },
    { value: 'MentionComment', label: 'Comentários em menções', isLocked: false, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
  ],
  linkedin: [
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
    { value: 'Mention', label: 'Menções', isLocked: false, checked: true },
    { value: 'MentionComment', label: 'Comentários em menções', isLocked: false, checked: true },
  ],
  youtube: [
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
  ],
  googleplay: [
    { value: 'Review', label: 'Avaliações', isLocked: true, checked: true },
    { value: 'ReviewComment', label: 'Comentários em avaliações', isLocked: true, checked: true },
  ],
  googlemybusiness: [
    { value: 'Review', label: 'Avaliações', isLocked: true, checked: true },
  ],
  threads: [
    { value: 'Post', label: 'Publicações Proprietárias', isLocked: true, checked: true },
    { value: 'PostComment', label: 'Comentários em publicações', isLocked: true, checked: true },
  ],
};

type ChannelLike = {
  key: string;
  support?: {
    sac_types?: unknown;
  };
};

interface InteractionLabelConfig {
  i18nKey: string;
  defaultLabel: string;
}

const normalizeChannelKey = (value: string): string => value.trim().toLowerCase();

const normalizeInteractionKey = (value: unknown): string =>
  (typeof value === 'string' ? value : '').trim().toLowerCase().replaceAll(/[^a-z0-9]/g, '');

const normalizeInteractionValue = (value: unknown): string => (typeof value === 'string' ? value : '').trim();

const toCanonicalInteractionValue = (value: unknown): string => {
  const rawValue = normalizeInteractionValue(value);
  const normalizedValue = normalizeInteractionKey(rawValue);

  if (normalizedValue === 'comment' || normalizedValue === 'postcomment') {
    return 'PostComment';
  }

  return rawValue;
};

const interactionLabelByValue = Object.values(interactionsByChannel).flat().reduce<Record<string, string>>(
  (acc, interaction) => {
    const normalizedValue = normalizeInteractionKey(interaction.value);
    if (normalizedValue && !acc[normalizedValue]) {
      acc[normalizedValue] = interaction.label;
    }
    return acc;
  },
  {},
);

const interactionLabelConfigByValue: Record<string, InteractionLabelConfig> = {
  post: {
    i18nKey: 'globalFilter.conversationOptions.interactions.proprietaryPosts',
    defaultLabel: 'Publicações proprietárias',
  },
  reels: {
    i18nKey: 'globalFilter.conversationOptions.interactions.reels',
    defaultLabel: 'Reels',
  },
  comment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.comments',
    defaultLabel: 'Comentários',
  },
  postcomment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.commentsOnPosts',
    defaultLabel: 'Comentários em publicações',
  },
  mention: {
    i18nKey: 'globalFilter.conversationOptions.interactions.mentions',
    defaultLabel: 'Menções',
  },
  mentioncomment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.mentionComments',
    defaultLabel: 'Comentários em menções',
  },
  externalcomment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.externalComments',
    defaultLabel: 'Comentários externos',
  },
  tagged: {
    i18nKey: 'globalFilter.conversationOptions.interactions.externalMentions',
    defaultLabel: 'Menções externas',
  },
  inbox: {
    i18nKey: 'globalFilter.conversationOptions.interactions.inbox',
    defaultLabel: 'Inbox',
  },
  visitorpost: {
    i18nKey: 'globalFilter.conversationOptions.interactions.visitorPosts',
    defaultLabel: 'Publicações de visitantes',
  },
  visitorcomment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.visitorPostComments',
    defaultLabel: 'Comentários em publicações de visitantes',
  },
  review: {
    i18nKey: 'globalFilter.conversationOptions.interactions.reviews',
    defaultLabel: 'Avaliações',
  },
  reviewcomment: {
    i18nKey: 'globalFilter.conversationOptions.interactions.reviewComments',
    defaultLabel: 'Comentários de avaliação',
  },
};

const resolveInteractionDefaultLabel = (value: string): string => {
  const normalizedValue = normalizeInteractionKey(value);
  return interactionLabelConfigByValue[normalizedValue]?.defaultLabel
    ?? interactionLabelByValue[normalizedValue]
    ?? value;
};

export const getInteractionLabelConfigByValue = (value: string): InteractionLabelConfig => {
  const normalizedValue = normalizeInteractionKey(value);
  return interactionLabelConfigByValue[normalizedValue] ?? {
    i18nKey: '',
    defaultLabel: resolveInteractionDefaultLabel(value),
  };
};

const interactionOrderByValue = Object.values(interactionsByChannel).flat().reduce<Record<string, number>>(
  (acc, interaction, index) => {
    const normalizedValue = normalizeInteractionKey(interaction.value);
    if (normalizedValue && acc[normalizedValue] === undefined) {
      acc[normalizedValue] = index;
    }
    return acc;
  },
  {},
);

const buildInteractionOptions = (
  channels: ChannelLike[],
  selectedChannels: string[] = [],
  labelResolver?: (interactionValue: string, defaultLabel: string) => string,
): Array<{ key: string; label: string }> => {
  const selectedSet = selectedChannels.length > 0
    ? new Set(selectedChannels.map((channel) => normalizeChannelKey(channel)))
    : null;

  const collected = new Map<string, string>();

  channels.forEach((channel) => {
    const channelKey = normalizeChannelKey(channel.key);
    if (!channelKey || (selectedSet && !selectedSet.has(channelKey))) return;

    const sacTypes = Array.isArray(channel.support?.sac_types) ? channel.support?.sac_types : [];
    sacTypes.forEach((sacType) => {
      const rawValue = toCanonicalInteractionValue(sacType);
      const normalizedValue = normalizeInteractionKey(rawValue);
      if (!rawValue || !normalizedValue || collected.has(normalizedValue)) return;
      collected.set(normalizedValue, rawValue);
    });
  });

  if (collected.size === 0) {
    const fallbackChannels = selectedChannels.length > 0 ? selectedChannels : Object.keys(interactionsByChannel);
    fallbackChannels.forEach((channelKey) => {
      const normalizedChannelKey = normalizeChannelKey(channelKey);
      (interactionsByChannel[normalizedChannelKey] ?? []).forEach((interaction) => {
        const normalizedValue = normalizeInteractionKey(interaction.value);
        if (!normalizedValue || collected.has(normalizedValue)) return;
        collected.set(normalizedValue, interaction.value);
      });
    });
  }

  return Array.from(collected.entries())
    .sort(([left], [right]) => {
      const leftRank = interactionOrderByValue[left] ?? Number.MAX_SAFE_INTEGER;
      const rightRank = interactionOrderByValue[right] ?? Number.MAX_SAFE_INTEGER;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return left.localeCompare(right);
    })
    .map(([, rawValue]) => ({
      key: rawValue,
      label: labelResolver
        ? labelResolver(rawValue, resolveInteractionDefaultLabel(rawValue))
        : resolveInteractionDefaultLabel(rawValue),
    }));
};

export const getInteractionOptionsFromChannels = (
  channels: ChannelLike[],
  selectedChannels: string[] = [],
  labelResolver?: (interactionValue: string, defaultLabel: string) => string,
): Array<{ key: string; label: string }> => buildInteractionOptions(channels, selectedChannels, labelResolver);

export const getInteractionValuesFromChannels = (
  channels: ChannelLike[],
  selectedChannels: string[] = [],
): string[] => buildInteractionOptions(channels, selectedChannels).map((interaction) => interaction.key);
